/**
 * Centralized reference + SKU builder (005 contracts/export.md, FR-005).
 *
 * The SINGLE source of truth for output paths and SKUs. Local refs (no host)
 * and future hosted CDN URLs differ ONLY by the host prefix (SC-003), so the
 * path scheme maps 1:1 onto the CDN. Also handles deterministic slug dedup so
 * duplicate design filenames stay unique (FR / edge cases).
 */
import type { ViewName } from "@genz/platform";

export type Ext = "jpg" | "png" | "webp";

/**
 * Which path scheme `buildRef` produces.
 *  - `legacy`  : `customer/product/[templateName]/phoneSlug/caseStyleSlug/[category]/designSlug/view.ext`
 *                (design-as-folder, view-as-file — the original, unchanged for existing callers).
 *  - `mockups` : `<Model>/<DesignName> - [<Template> - ]<View>.<ext>`
 *                (one folder per model, every file directly inside it — human-readable,
 *                casing/spaces preserved).
 */
export type RefScheme = "legacy" | "mockups";

/**
 * Per-segment character cap. NTFS/APFS/ext4 all cap a single name at 255 *bytes*; 120
 * chars leaves room for multi-byte UTF-8 and for the `-2`/`~hash` dedup suffixes we add.
 */
export const MAX_SEGMENT_CHARS = 120;

/**
 * Cap for the whole RELATIVE ref. Windows' classic `MAX_PATH` is 260 chars for the FULL
 * path, and the operator's chosen destination folder (+ the `Mockups/` bundle root) is
 * prepended to everything we emit — so the relative part gets 200 and the destination
 * keeps ~60. Beyond this Windows refuses the write and the export silently loses files.
 */
export const MAX_PATH_CHARS = 200;

/**
 * Windows reserved DEVICE names. A file called `CON`, `NUL`, `COM1`, … (with or without an
 * extension, any casing) cannot be created on Windows — the write fails, so a design named
 * "con.png" or a brand folder called "AUX" silently dropped that file from the export.
 */
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

/** Deterministic 4-hex-char digest, used to keep TRUNCATED names unique (never a collision). */
function shortHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) h = Math.imul(h ^ input.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(16).padStart(8, "0").slice(0, 4);
}

export interface RefParts {
  customer: string;
  product: string;
  /** Template label → its own path segment so different templates export to different folders
   *  — not just customer/product. `legacy` slugifies it; `mockups` keeps it human (pathSafe).
   *  Empty/omitted → the segment is skipped. Callers MUST pass a value that is unique across
   *  the run (two templates can share a name — append the template id then). */
  templateName?: string;
  phoneSlug: string;
  caseStyleSlug: string;
  designSlug: string;
  view: ViewName | string;
  ext: Ext | string;
  /** Optional design category/subfolder (spec 014). When set, it is inserted as a
   *  path segment before the design so the source folder structure is preserved. */
  category?: string;
  /** When provided, returns a hosted CDN URL (Phase 3); else a local path. */
  host?: string;
  /** Path scheme (default `legacy`). `mockups` produces the human-readable
   *  Model/DesignName - [Template - ]View.ext layout. */
  scheme?: RefScheme;
  /** Mockups scheme — human `<Product Type>` folder (e.g. "Transparent Phone Cases"). */
  productType?: string;
  /** Mockups scheme — human `<Brand>` folder (e.g. "OnePlus"). */
  brand?: string;
  /** Mockups scheme — human `<Model>` folder (e.g. "OnePlus 6"). */
  model?: string;
  /** Mockups scheme — the design FILE base name, used VERBATIM (path-safed, never
   *  re-stripped). Build it with {@link designFileBase} and de-dup collisions with
   *  `dedupeSlugs` BEFORE passing it. Falls back to `designSlug`. */
  designName?: string;
}

/**
 * Make a raw label safe to use as ONE filesystem path segment WITHOUT slugifying:
 * human casing and spaces are preserved (e.g. "OnePlus 6", "Front", "Marvel_Spiderman"),
 * only the characters illegal in a path segment `< > : " / \ | ? *` are stripped, plus
 * control chars, collapsed whitespace, and trimmed leading/trailing dots/spaces (Windows).
 * Empty/blank input → "untitled" so a segment is never empty.
 *
 * Also guards the two Windows rules a pure character filter misses (both silently DROPPED
 * files from an export before, because the write simply failed):
 *  - reserved DEVICE names (`CON`, `NUL`, `COM1`…, with or without an extension) get a `_`
 *    suffix, which is what Explorer itself does;
 *  - an over-long segment is truncated to {@link MAX_SEGMENT_CHARS} with a deterministic
 *    `~hash` tail, so two long names that share a prefix still land on distinct files.
 */
export function pathSafe(input: string, maxLength: number = MAX_SEGMENT_CHARS): string {
  const trim = (s: string) => s.replace(/^[\s.]+|[\s.]+$/g, "");
  let cleaned = trim(
    (input ?? "")
      // Strip the filesystem-illegal chars < > : " / \ | ? * plus ASCII control chars.
      // Spaces are PRESERVED (they are NOT in the class).
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, "")
      // Collapse internal whitespace runs to a single space.
      .replace(/\s+/g, " "),
  );
  if (!cleaned) return "untitled";
  // Reserved device name → suffix it. Checked AFTER cleaning so "CON " / "con." are caught.
  if (WINDOWS_RESERVED.test(cleaned)) cleaned = `${cleaned}_`;
  if (cleaned.length > maxLength) {
    // Keep the head (human-recognizable) + a digest of the FULL name so truncation can
    // never merge two distinct designs onto one output file.
    const head = trim(cleaned.slice(0, Math.max(1, maxLength - 5)));
    cleaned = `${head}~${shortHash(cleaned)}`;
  }
  return cleaned || "untitled";
}

/** Strip a trailing file extension from a filename, leaving the base name intact. */
export function stripExt(name: string): string {
  return name.replace(/\.[a-z0-9]+$/i, "");
}

/** The extensions an uploaded DESIGN can actually have (mirrors `IMAGE_ACCEPT` in
 *  @genz/platform). Anything else after the last dot is part of the name. */
const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|tiff?|avif|heic|heif)$/i;

/**
 * Strip a trailing IMAGE extension only — unlike {@link stripExt}, which eats ANY trailing
 * `.token`.
 *
 * P0 data loss: the mockups path called `stripExt` on `designName`, which callers had
 * ALREADY stripped (`run.ts` → `baseName`). The second pass then ate the real tail of the
 * name, so "logo.v1" and "logo.v2" both became "logo" — two distinct designs wrote the same
 * output file and the operator silently received one of them. Only a genuine image
 * extension may be removed here; a version-looking suffix is part of the design's identity.
 */
export function stripImageExt(name: string): string {
  return name.replace(IMAGE_EXT, "");
}

/**
 * Turn an uploaded design's FILENAME into the path-safe FILE base `designName` expects.
 *
 * This is the ONE place the extension comes off. `buildRef` deliberately does not strip
 * again: stripping is not idempotent ("photo.png.png" → "photo.png" → "photo"), so a second
 * pass could re-merge names its caller had already separated and de-duplicated. Callers
 * de-dupe over THIS value, which is exactly what lands on disk.
 */
export function designFileBase(filename: string): string {
  return pathSafe(stripImageExt(filename));
}

/** Title-case each word for a human folder label (e.g. view "front" → "Front"). Preserves
 *  already-capitalized input; used only for the mockups View folder so the tree reads nicely. */
function titleCase(label: string): string {
  return label.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Slugify a raw filename/label deterministically. */
export function slugify(input: string): string {
  return (
    input
      .normalize("NFKD")
      .replace(/\.[a-z0-9]+$/i, "") // drop extension
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-") || "design"
  );
}

/**
 * Build the canonical reference path. With `host`, prefixes
 * `https://cdn.<host>/`; the trailing path is identical either way (SC-003).
 *
 * Two schemes (see {@link RefScheme}). The default `legacy` scheme is unchanged for
 * every existing caller. The `mockups` scheme produces the human-readable
 * `<Model>/<DesignName> - [<Template> - ]<View>.<ext>` layout, with casing/spaces preserved.
 */
export function buildRef(parts: RefParts): string {
  const { view, ext, category, host } = parts;
  const path = capPathLength(
    parts.scheme === "mockups" ? buildMockupsPath(parts) : buildLegacyPath(parts),
  );
  return host ? `https://cdn.${host}/${path}` : path;

  // --- helpers (scoped so the two schemes read side-by-side) ---------------
  function buildLegacyPath(p: RefParts): string {
    // Build from NON-EMPTY segments only — a generated template has no phone/case slug, which
    // otherwise left empty `//` segments in the path. Include the template name (when set) so
    // different templates export to different folders.
    const segs = [
      p.customer,
      p.product,
      p.templateName ? slugify(p.templateName) : "",
      p.phoneSlug,
      p.caseStyleSlug,
      category ? slugify(category) : "",
      p.designSlug,
    ].filter(Boolean);
    return `${segs.join("/")}/${view}.${ext}`;
  }

  function buildMockupsPath(p: RefParts): string {
    // ONE folder per model, every file directly inside it (owner rule 2026-09-26): product
    // type, brand, template, design category and view are not folders. The file name carries
    // what keeps it unique inside that folder — the design (callers de-dup it across the run),
    // the template (two templates for one model must not share a file, ST-1) and the view.
    // `designName` is taken VERBATIM: callers own the extension strip (see `designFileBase`).
    const folder = pathSafe(p.model || p.productType || p.product);
    const nameParts = collapseRepeats(
      [p.designName ?? p.designSlug, p.templateName ?? "", titleCase(String(view))]
        .filter(Boolean)
        .map((part) => pathSafe(part)),
    );
    return `${folder}/${pathSafe(nameParts.join(FILE_PART_SEP))}.${ext}`;
  }
}

const FILE_PART_SEP = " - ";

/**
 * The print file that sits beside a mock-up in its model folder: the mock-up's name with its
 * view part swapped for "Print" (`Swirl - Glossy - Front.jpg` → `Swirl - Glossy - Print.jpg`).
 * A name shortened by the path cap has lost its view part, so "Print" is appended instead.
 */
export function printRefFor(localRef: string, view: string): string {
  const slash = localRef.lastIndexOf("/");
  const dot = localRef.lastIndexOf(".");
  const stemEnd = dot > slash ? dot : localRef.length;
  const stem = localRef.slice(0, stemEnd);
  const viewPart = `${FILE_PART_SEP}${pathSafe(titleCase(view))}`;
  const base = stem.endsWith(viewPart) ? stem.slice(0, -viewPart.length) : stem;
  return `${base}${FILE_PART_SEP}Print${localRef.slice(stemEnd)}`;
}

/**
 * Drop a file-name part that merely repeats the one directly before it, case-insensitively.
 *
 * The parts come from different fields that can legitimately resolve to the SAME name (F-7),
 * and "Swirl - Back - Back" reads as a bug. It can never merge two DIFFERENT outputs onto one
 * path: a part it removes duplicates a part that is still there.
 */
function collapseRepeats(segs: string[]): string[] {
  const out: string[] = [];
  for (const seg of segs) {
    if (out.length > 0 && out[out.length - 1].toLowerCase() === seg.toLowerCase()) continue;
    out.push(seg);
  }
  return out;
}

/**
 * Keep the relative ref inside {@link MAX_PATH_CHARS} (Windows `MAX_PATH`). Only the FILE
 * base name is shortened — folders stay intact so the tree still reads correctly — and a
 * `~hash` of the full base keeps two long, same-prefixed designs on distinct files. When the
 * FOLDERS alone already blow the budget we return the path unchanged: a failed write is a
 * visible error, whereas silently colliding directory names would be another data loss.
 */
function capPathLength(path: string): string {
  if (path.length <= MAX_PATH_CHARS) return path;
  const slash = path.lastIndexOf("/");
  const dir = slash >= 0 ? path.slice(0, slash + 1) : "";
  const file = path.slice(slash + 1);
  const dot = file.lastIndexOf(".");
  const base = dot > 0 ? file.slice(0, dot) : file;
  const ext = dot > 0 ? file.slice(dot) : "";
  const room = MAX_PATH_CHARS - dir.length - ext.length - 5; // 5 = "~" + 4 hex
  if (room < 4) return path;
  return `${dir}${base.slice(0, room)}~${shortHash(base)}${ext}`;
}

export interface SkuParts {
  phoneSlug: string;
  caseStyleSlug: string;
  designSlug: string;
}

/**
 * `${PHONE}-${CASE}-${DESIGN}` uppercased and deduped. "Deduped" = collapse
 * repeated tokens so e.g. phone "iphone-15" + case "iphone-15-clear" doesn't
 * repeat "IPHONE-15"; tokens are uppercased and joined with single hyphens.
 */
export function buildSku(parts: SkuParts): string {
  const tokens: string[] = [];
  const seen = new Set<string>();
  const push = (slug: string) => {
    for (const tok of slug.toUpperCase().split("-").filter(Boolean)) {
      if (!seen.has(tok)) {
        seen.add(tok);
        tokens.push(tok);
      }
    }
  };
  push(parts.phoneSlug);
  push(parts.caseStyleSlug);
  push(parts.designSlug);
  return tokens.join("-");
}

/**
 * Deduplicate a list of design slugs deterministically: the first occurrence
 * keeps its slug; subsequent collisions get a stable `-2`, `-3`, ... suffix.
 */
export function dedupeSlugs(slugs: string[]): string[] {
  const counts = new Map<string, number>();
  return slugs.map((slug) => {
    const n = counts.get(slug) ?? 0;
    counts.set(slug, n + 1);
    return n === 0 ? slug : `${slug}-${n + 1}`;
  });
}

/** Convert local refs to hosted CDN URLs (Phase 3) by adding the host prefix only. */
export function toHostedUrl(localRef: string, host: string): string {
  return `https://cdn.${host}/${localRef}`;
}

/**
 * Derive a design category from a picked file's folder path (spec 014). The immediate
 * parent folder is the category (e.g. `Designs/Abstract/x.png` → `abstract`). A bare
 * filename with no folder yields `undefined` (no category).
 */
export function categoryFromPath(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  const parts = path.split(/[\\/]/).filter(Boolean);
  if (parts.length < 2) return undefined; // just a filename — no enclosing folder
  return slugify(parts[parts.length - 2]);
}
